import React, { useEffect, useState } from 'react';
import { Props } from './Future.types';
import css from './Future.module.scss';
import validationSchema from './validationSchema';
import { FuturePension } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { EditableField } from 'modules/cm/fields/EditableField';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { Box, Columns, Icon } from 'modules/af_cm_ui_kit/components';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { useDispatch } from 'react-redux';
import { Resources } from 'modules/cm/pages/CaseDetailsPage/subPages/Applicant/Expenditure/Expenditure.types';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import ErrorMessage from 'shared/Messages/ErrorMessage';
import { FieldWithError } from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError/FieldWithError';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../../form/yup/reactHookFormResolver';
import { definedBenefitsApi, definedContributionsApi, futureAnnuityApi } from '../../../../../../api';
import { fromApiMapper } from '../../../../../../store/cases/mapper';
import { updateApplicant } from '../../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../../hooks/useSaveButtonClicked';
import { saveCase, setErrorModal } from '../../../../../../store/cases/actions';
import * as subPages from '../../../../../../store/subPages';
import { SUBPAGES } from '../../../../../../store/subPages/types';
import CheckDirty from '../../../../../../components/CheckDirty/CheckDirty';

type DefinedBenefit = FuturePension[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX][0];
type DefinedContribution = FuturePension[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX][0];
type FutureAnnuity = FuturePension[FIELDS.FUTURE_ANNUITY_PREFIX][0];

type FieldArrayChildProps<T> = {
  onDelete: () => void;
  item: T;
  index: number;
  resources: Resources;
  isCaseReadOnly: boolean;
  modulePrefix: string;
} & FieldWithError;

const UpdateableField = ({
  isUpdateable = true,
  isCaseReadOnly,
  ...props
}: React.ComponentProps<typeof EditableField>) => {
  return (
    <EditableField
      {...props}
      readOnly={!isUpdateable}
      disabled={isCaseReadOnly}
      extraContent={[
        ...props.getErrors(props.field.name).map((error: string) => ({
          position: 'bottom',
          variant: 'component',
          content: error ? <ErrorMessage message={error} /> : '',
        })),
        ...(props.extraContent || []),
      ]}
    />
  );
};

const DefinedContributions = ({
  modulePrefix,
  item,
  index,
  resources,
  onDelete,
  isCaseReadOnly,
  getErrors,
}: FieldArrayChildProps<DefinedContribution>) => {
  return (
    <React.Fragment>
      <div className={css.actionsRow}>
        <div className={css.content}>
          <UpdateableField
            modulePrefix={modulePrefix}
            fieldType={INPUTS.TEXT}
            field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.NAME_OF_SCHEME]}
            isCaseReadOnly={isCaseReadOnly}
            getErrors={getErrors}
          />
        </div>
        <div className={css.actions}>
          <button type="button" className={css.button} onClick={() => onDelete()} disabled={isCaseReadOnly}>
            <Icon type="trash" />
          </button>
        </div>
      </div>

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.CURRENT_VALUE]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.START_DATE]}
        variant={VARIANTS_TYPES.DATE}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.ONGOING_MONTHLY_CONTRIBUTIONS]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={
          fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH]
        }
        variant={VARIANTS_TYPES.PERCENTAGE}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />
      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.FUTURE_ANNUAL_INCOME_DRAWN]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.SELECT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX](index)[FIELDS.USE_OF_FUND]}
        options={resources.useOfFunds}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
        required
      />
    </React.Fragment>
  );
};

const DefinedBenefits = ({
  modulePrefix,
  getErrors,
  item,
  index,
  onDelete,
  isCaseReadOnly,
}: FieldArrayChildProps<DefinedBenefit>) => {
  return (
    <React.Fragment>
      <div className={css.actionsRow}>
        <div className={css.content}>
          <UpdateableField
            modulePrefix={modulePrefix}
            fieldType={INPUTS.TEXT}
            field={fields[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX](index)[FIELDS.NAME_OF_SCHEME]}
            isCaseReadOnly={isCaseReadOnly}
            getErrors={getErrors}
          />
        </div>
        <div className={css.actions}>
          <button type="button" className={css.button} onClick={() => onDelete()} disabled={isCaseReadOnly}>
            <Icon type="trash" />
          </button>
        </div>
      </div>

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX](index)[FIELDS.ANNUAL_INCOME]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX](index)[FIELDS.START_DATE]}
        variant={VARIANTS_TYPES.DATE}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />

      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX](index)[FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH]}
        variant={VARIANTS_TYPES.PERCENTAGE}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />
      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX](index)[FIELDS.FUTURE_PENSION_REPAYMENT_LUMP_SUM]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />
    </React.Fragment>
  );
};

const FuturePensionsAnnuity = ({
  modulePrefix,
  getErrors,
  item,
  index,
  onDelete,
  isCaseReadOnly,
}: FieldArrayChildProps<FutureAnnuity>) => {
  return (
    <React.Fragment>
      <div className={css.actionsRow}>
        <div className={css.content}>
          <UpdateableField
            modulePrefix={modulePrefix}
            fieldType={INPUTS.TEXT}
            field={fields[FIELDS.FUTURE_ANNUITY_PREFIX](index)[FIELDS.FUTURE_ANNUITY_NAME_OF_ANNUITY_PROVIDER]}
            isCaseReadOnly={isCaseReadOnly}
            getErrors={getErrors}
          />
        </div>
        <div className={css.actions}>
          <button type="button" className={css.button} onClick={() => onDelete()} disabled={isCaseReadOnly}>
            <Icon type="trash" />
          </button>
        </div>
      </div>
      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_ANNUITY_PREFIX](index)[FIELDS.FUTURE_ANNUITY_GROSS_ANNUAL_INCOME]}
        variant={VARIANTS_TYPES.CURRENCY}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />
      <UpdateableField
        modulePrefix={modulePrefix}
        fieldType={INPUTS.TEXT}
        field={fields[FIELDS.FUTURE_ANNUITY_PREFIX](index)[FIELDS.FUTURE_ANNUITY_PERCENT_PASSED_ON_DEATH]}
        variant={VARIANTS_TYPES.PERCENTAGE}
        isCaseReadOnly={isCaseReadOnly}
        getErrors={getErrors}
      />
    </React.Fragment>
  );
};

const Future: React.FC<Props> = ({
  modulePrefix,
  clearErrors,
  getErrors,
  resources,
  caseId,
  applicantId,
  data,
  initialData,
  applicantIndex,
}) => {
  const dispatch = useDispatch();
  const isCaseReadOnly = useCaseIsReadOnly();
  const defaultValues: FuturePension = {
    [FIELDS.FUTURE_STATE_PENSION]: initialData[FIELDS.FUTURE_STATE_PENSION],
    [FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX]: initialData[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX],
    [FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX]:
      initialData[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX],
    [FIELDS.FUTURE_ANNUITY_PREFIX]: initialData[FIELDS.FUTURE_ANNUITY_PREFIX],
    [FIELDS.LUMP_SUM_DRAWDOWN]: initialData[FIELDS.LUMP_SUM_DRAWDOWN],
    [FIELDS.REPAYMENT_LUMP_SUM]: initialData[FIELDS.REPAYMENT_LUMP_SUM],
  };
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const methods = useForm<FuturePension>({
    mode: 'onBlur',
    defaultValues,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { control, reset } = methods;

  useEffect(() => {
    reset(
      {
        [FIELDS.FUTURE_STATE_PENSION]: data[FIELDS.FUTURE_STATE_PENSION],
        [FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX]: data[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX],
        [FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX]: data[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX],
        [FIELDS.FUTURE_ANNUITY_PREFIX]: data[FIELDS.FUTURE_ANNUITY_PREFIX],
      },
      {
        keepDefaultValues: currentApplicantIndex === applicantIndex,
      },
    );
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const {
    fields: definedContributions,
    append: appendDefinedContributionField,
    remove: removeDefinedContributionField,
  } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX,
  });
  const {
    fields: definedBenefits,
    append: appendDefinedBenefitField,
    remove: removeDefinedBenefitField,
  } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX,
  });
  const {
    fields: futureAnnuities,
    append: appendFutureAnnuitiesField,
    remove: removeFutureAnnuitiesField,
  } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.FUTURE_ANNUITY_PREFIX,
  });

  const addDefinedBenefit = () => {
    definedBenefitsApi(caseId)(applicantId)
      .create()
      .then((res) => {
        appendDefinedBenefitField(fromApiMapper.mapFuturePensionDefinedBenefit(res.data));
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };
  const addDefinedContribution = () => {
    definedContributionsApi(caseId)(applicantId)
      .create()
      .then((res) => {
        appendDefinedContributionField(fromApiMapper.mapFuturePensionDefinedContribution(res.data));
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const addAnnuity = () => {
    futureAnnuityApi(caseId)(applicantId)
      .create()
      .then((res) => {
        appendFutureAnnuitiesField(fromApiMapper.mapFutureAnnuity(res.data));
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const handleDeleteDefinedContribution = (item: any, index: number) => {
    definedContributionsApi(caseId)(applicantId)
      .remove(item.id)
      .then(() => {
        removeDefinedContributionField(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const handleDeleteDefinedBenefit = (item: any, index: number) => {
    definedBenefitsApi(caseId)(applicantId)
      .remove(item.id)
      .then(() => {
        removeDefinedBenefitField(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const handleDeleteAnnuity = (item: any, index: number) => {
    futureAnnuityApi(caseId)(applicantId)
      .remove(item.id)
      .then(() => {
        removeFutureAnnuitiesField(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  return (
    <>
      <FormProvider<FuturePension> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(
                applicantIndex,
                SUBPAGES.APPLICANT_PENSIONS_FUTURE,
                isDirty,
              ),
            );
          }}
        />
        <form onSubmit={() => {}} className={css.wrapper}>
          <Columns spacing={4}>
            <>
              <Box title="State Pension Income">
                <UpdateableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.FUTURE_STATE_PENSION]}
                  variant={VARIANTS_TYPES.CURRENCY}
                  isCaseReadOnly={isCaseReadOnly}
                  getErrors={getErrors}
                />
              </Box>

              <Box title="Defined Contribution">
                <UpdateableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LUMP_SUM_DRAWDOWN]}
                  variant={VARIANTS_TYPES.PERCENTAGE}
                  isCaseReadOnly={isCaseReadOnly}
                  getErrors={getErrors}
                />

                <UpdateableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.REPAYMENT_LUMP_SUM]}
                  variant={VARIANTS_TYPES.PERCENTAGE}
                  isCaseReadOnly={isCaseReadOnly}
                  getErrors={getErrors}
                />

                {definedContributions.length ? <hr style={{ margin: '32px 0px' }} /> : undefined}

                {definedContributions.map((field, index: number) => (
                  <div key={field.key}>
                    <DefinedContributions
                      modulePrefix={modulePrefix}
                      resources={resources}
                      onDelete={() => handleDeleteDefinedContribution(field, index)}
                      isCaseReadOnly={isCaseReadOnly}
                      getErrors={getErrors}
                      item={field as any}
                      index={index}
                    />
                    {index !== definedContributions.length - 1 && <hr className={css.divider} />}
                  </div>
                ))}

                <hr />

                <button
                  type="button"
                  className={css.button}
                  disabled={isCaseReadOnly}
                  onClick={addDefinedContribution}
                  style={{ marginTop: 32, marginBottom: -16 }}>
                  Add {methods.getValues()[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX].length ? 'another' : ''}{' '}
                  defined contribution
                </button>
              </Box>
            </>
            <>
              <Box title="Defined Benefit">
                {definedBenefits.map((field, index: number) => (
                  <>
                    <DefinedBenefits
                      key={field.key}
                      modulePrefix={modulePrefix}
                      resources={resources}
                      onDelete={() => handleDeleteDefinedBenefit(field, index)}
                      isCaseReadOnly={isCaseReadOnly}
                      getErrors={getErrors}
                      item={field as any}
                      index={index}
                    />
                    {index !== definedBenefits.length - 1 && <hr className={css.divider} />}
                  </>
                ))}

                <hr />

                <button
                  type="button"
                  className={css.button}
                  onClick={addDefinedBenefit}
                  disabled={isCaseReadOnly}
                  style={{ marginTop: 32, marginBottom: -16 }}>
                  Add {methods.getValues()[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX].length ? 'another' : ''}{' '}
                  defined benefit
                </button>
              </Box>
              <Box title="Annuity">
                {futureAnnuities.map((field, index: number) => (
                  <>
                    <FuturePensionsAnnuity
                      key={field.key}
                      modulePrefix={modulePrefix}
                      resources={resources}
                      onDelete={() => handleDeleteAnnuity(field, index)}
                      isCaseReadOnly={isCaseReadOnly}
                      getErrors={getErrors}
                      item={field as any}
                      index={index}
                    />
                    {index !== futureAnnuities.length - 1 && <hr className={css.divider} />}
                  </>
                ))}

                <hr />
                <button
                  type="button"
                  className={css.button}
                  onClick={addAnnuity}
                  disabled={isCaseReadOnly}
                  style={{ marginTop: 32, marginBottom: -16 }}>
                  Add {methods.getValues()[FIELDS.FUTURE_ANNUITY_PREFIX].length ? 'another' : ''} annuity
                </button>
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>
    </>
  );
};

export default Future;
